import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Badge, Button, Alert, Spinner, Carousel, Breadcrumb } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt,
  faBed,
  faBath,
  faRuler,
  faCalendarAlt,
  faCoins,
  faPhone,
  faEnvelope,
  faArrowLeft,
  faHeart,
  faShare,
  faPrint,
  faHome,
  faSwimmingPool,
  faCar,
  faFire,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import PropertyDetailsModal from '../components/PropertyDetailsModal';

interface PropertyData {
  id: string;
  mlsNumber: string;
  price: number;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  bedrooms: number;
  bathrooms: number;
  sqft?: number;
  yearBuilt?: number;
  propertyType: string;
  description?: string;
  features?: string[];
  images?: string[];
  listingAgent?: {
    name: string;
    phone?: string;
    email?: string;
    officeName?: string;
  };
  daysOnMarket?: number;
  pool?: boolean;
  garage?: number;
  fireplace?: boolean;
  lotSize?: number;
  status?: string;
  listingDate?: string;
  propertySubType?: string;
  virtualTour?: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

const PropertyDetail: React.FC = () => {
  const { mlsNumber } = useParams<{ mlsNumber: string }>();
  const navigate = useNavigate();
  const [property, setProperty] = useState<PropertyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isSaved, setIsSaved] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [similarProperties, setSimilarProperties] = useState<PropertyData[]>([]);

  useEffect(() => {
    if (mlsNumber) {
      fetchPropertyDetails();
      checkIfSaved();
    }
  }, [mlsNumber]);

  const fetchPropertyDetails = async () => {
    try {
      setLoading(true);
      
      // Try to get from live ARMLS first
      let response;
      try {
        response = await axios.get(
          `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'http://localhost:8080'}/api/mls-portal/properties/live/test`,
          {
            params: {
              mlsNumber: mlsNumber,
              limit: 1
            }
          }
        );
        
        if (response.data.success && response.data.data && response.data.data.length > 0) {
          const propertyData = response.data.data[0];
          setProperty(propertyData);
          fetchSimilarProperties(propertyData);
        } else {
          throw new Error('Property not found in live data');
        }
      } catch (liveError) {
        // Fallback to database - try the same endpoint without specific MLS number
        response = await axios.get(
          `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'http://localhost:8080'}/api/mls-portal/properties/live/test`,
          {
            params: {
              limit: 20
            }
          }
        );
        
        if (response.data.success && response.data.data) {
          setProperty(response.data.data);
          fetchSimilarProperties(response.data.data);
        } else {
          setError('Property not found');
        }
      }
    } catch (err: any) {
      console.error('Error fetching property:', err);
      setError('Failed to load property details');
    } finally {
      setLoading(false);
    }
  };

  const checkIfSaved = async () => {
    const token = localStorage.getItem('authToken');
    if (!token || !mlsNumber) return;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'http://localhost:8080'}/api/mls-portal/actions/check-saved`,
        { mlsNumbers: [mlsNumber] },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success && response.data.data[mlsNumber]) {
        setIsSaved(true);
      }
    } catch (error) {
      console.error('Error checking saved status:', error);
    }
  };

  const fetchSimilarProperties = async (currentProperty: PropertyData) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'http://localhost:8080'}/api/mls-portal/properties/live/test`,
        {
          params: {
            city: currentProperty.address.city,
            minPrice: Math.floor(currentProperty.price * 0.8),
            maxPrice: Math.ceil(currentProperty.price * 1.2),
            bedrooms: currentProperty.bedrooms,
            limit: 4
          }
        }
      );

      if (response.data.success && response.data.data) {
        const similar = response.data.data
          .filter((p: PropertyData) => p.mlsNumber !== mlsNumber)
          .slice(0, 3);
        setSimilarProperties(similar);
      }
    } catch (error) {
      console.error('Error fetching similar properties:', error);
    }
  };

  const handleSaveToggle = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      alert('Please login to save properties');
      return;
    }

    try {
      const endpoint = isSaved 
        ? `mls-portal/actions/save/${mlsNumber}`
        : 'mls-portal/actions/save';
      
      const method = isSaved ? 'DELETE' : 'POST';
      
      await axios({
        method,
        url: `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api/'}${endpoint}`,
        data: !isSaved ? {
          mlsNumber: mlsNumber,
          propertyData: property
        } : undefined,
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setIsSaved(!isSaved);
    } catch (error) {
      console.error('Error toggling save:', error);
      alert('Failed to update saved property');
    }
  };

  const handlePrint = async () => {
    if (!property) return;
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'http://localhost:8080'}/api/mls-portal/actions/print`,
        { propertyData: property },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `property-${mlsNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error printing property:', error);
      alert('Failed to generate property sheet');
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <Spinner animation="border" role="status" />
          <p className="mt-3">Loading property details...</p>
        </div>
      </Container>
    );
  }

  if (error || !property) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <h4>Property Not Found</h4>
          <p>{error || 'The requested property could not be found.'}</p>
          <Button variant="primary" onClick={() => navigate('/mls-portal')}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Search
          </Button>
        </Alert>
      </Container>
    );
  }

  const allImages = property.images?.length ? property.images : [];

  return (
    <Container className="py-4">
      {/* Breadcrumb */}
      <Breadcrumb>
        <Breadcrumb.Item onClick={() => navigate('/mls-portal')}>
          MLS Portal
        </Breadcrumb.Item>
        <Breadcrumb.Item onClick={() => navigate('/mls-portal')}>
          Search
        </Breadcrumb.Item>
        <Breadcrumb.Item active>
          {property.address.street}
        </Breadcrumb.Item>
      </Breadcrumb>

      {/* Header with Actions */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h1 className="h3 mb-2">{property.address.street}</h1>
              <p className="text-muted mb-0">
                {property.address.city}, {property.address.state} {property.address.zip}
              </p>
              <h2 className="text-primary mt-2">{formatPrice(property.price)}</h2>
            </div>
            <div className="d-flex gap-2">
              <Button 
                variant={isSaved ? "primary" : "outline-primary"}
                onClick={handleSaveToggle}
              >
                <FontAwesomeIcon icon={faHeart} className="me-2" />
                {isSaved ? 'Saved' : 'Save'}
              </Button>
              <Button 
                variant="outline-primary"
                onClick={() => setShowShareModal(true)}
              >
                <FontAwesomeIcon icon={faShare} className="me-2" />
                Share
              </Button>
              <Button 
                variant="outline-primary"
                onClick={handlePrint}
              >
                <FontAwesomeIcon icon={faPrint} className="me-2" />
                Print
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        {/* Image Gallery */}
        <Col lg={8}>
          <Card className="mb-4">
            <Card.Body className="p-0">
              {allImages.length > 0 ? (
                <Carousel 
                  activeIndex={activeImageIndex}
                  onSelect={setActiveImageIndex}
                  className="property-carousel"
                >
                  {allImages.map((image: string, index: number) => (
                    <Carousel.Item key={index}>
                      <img
                        className="d-block w-100"
                        src={image}
                        alt={`Property ${index + 1}`}
                        style={{ maxHeight: '500px', objectFit: 'cover' }}
                      />
                    </Carousel.Item>
                  ))}
                </Carousel>
              ) : (
                <div className="text-center py-5 bg-light">
                  <FontAwesomeIcon icon={faHome} size="4x" className="text-muted" />
                  <p className="mt-3 text-muted">No images available</p>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Property Overview */}
          <Card className="mb-4">
            <Card.Body>
              <h5 className="mb-3">Property Overview</h5>
              <Row>
                <Col xs={6} md={3} className="mb-3">
                  <div className="text-center">
                    <FontAwesomeIcon icon={faBed} size="2x" className="text-primary mb-2" />
                    <p className="mb-0"><strong>{property.bedrooms}</strong></p>
                    <small className="text-muted">Bedrooms</small>
                  </div>
                </Col>
                <Col xs={6} md={3} className="mb-3">
                  <div className="text-center">
                    <FontAwesomeIcon icon={faBath} size="2x" className="text-primary mb-2" />
                    <p className="mb-0"><strong>{property.bathrooms}</strong></p>
                    <small className="text-muted">Bathrooms</small>
                  </div>
                </Col>
                <Col xs={6} md={3} className="mb-3">
                  <div className="text-center">
                    <FontAwesomeIcon icon={faRuler} size="2x" className="text-primary mb-2" />
                    <p className="mb-0"><strong>{property.sqft?.toLocaleString() || 'N/A'}</strong></p>
                    <small className="text-muted">Square Feet</small>
                  </div>
                </Col>
                <Col xs={6} md={3} className="mb-3">
                  <div className="text-center">
                    <FontAwesomeIcon icon={faCalendarAlt} size="2x" className="text-primary mb-2" />
                    <p className="mb-0"><strong>{property.yearBuilt || 'N/A'}</strong></p>
                    <small className="text-muted">Year Built</small>
                  </div>
                </Col>
              </Row>

              {/* Additional Features */}
              <div className="mt-3">
                {property.pool && (
                  <Badge bg="info" className="me-2">
                    <FontAwesomeIcon icon={faSwimmingPool} className="me-1" />
                    Pool
                  </Badge>
                )}
                {property.garage && property.garage > 0 && (
                  <Badge bg="secondary" className="me-2">
                    <FontAwesomeIcon icon={faCar} className="me-1" />
                    {property.garage} Car Garage
                  </Badge>
                )}
                {property.fireplace && (
                  <Badge bg="warning" text="dark" className="me-2">
                    <FontAwesomeIcon icon={faFire} className="me-1" />
                    Fireplace
                  </Badge>
                )}
              </div>
            </Card.Body>
          </Card>

          {/* Property Description */}
          {property.description && (
            <Card className="mb-4">
              <Card.Body>
                <h5>Description</h5>
                <p>{property.description}</p>
              </Card.Body>
            </Card>
          )}

          {/* Features */}
          {property.features && property.features.length > 0 && (
            <Card className="mb-4">
              <Card.Body>
                <h5>Features & Amenities</h5>
                <Row>
                  {property.features.map((feature, index) => (
                    <Col md={6} key={index} className="mb-2">
                      <FontAwesomeIcon icon={faCheck} className="text-success me-2" />
                      {feature}
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          )}
        </Col>

        {/* Property Details Sidebar */}
        <Col lg={4}>
          {/* Agent Information */}
          <Card className="mb-4">
            <Card.Body>
              <h5>Contact Agent</h5>
              {property.listingAgent ? (
                <>
                  <p className="mb-2">
                    <strong>{property.listingAgent.name}</strong>
                  </p>
                  {property.listingAgent.officeName && (
                    <p className="mb-2 text-muted">{property.listingAgent.officeName}</p>
                  )}
                  {property.listingAgent.phone && (
                    <div className="d-grid mb-2">
                      <Button variant="primary" href={`tel:${property.listingAgent.phone}`}>
                        <FontAwesomeIcon icon={faPhone} className="me-2" />
                        {property.listingAgent.phone}
                      </Button>
                    </div>
                  )}
                  {property.listingAgent.email && (
                    <div className="d-grid">
                      <Button variant="outline-primary" href={`mailto:${property.listingAgent.email}`}>
                        <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                        Email Agent
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-muted">Contact information not available</p>
              )}
            </Card.Body>
          </Card>

          {/* Property Details */}
          <Card className="mb-4">
            <Card.Body>
              <h5>Property Details</h5>
              <table className="table table-sm">
                <tbody>
                  <tr>
                    <td>Property Type:</td>
                    <td>{property.propertyType}</td>
                  </tr>
                  {property.propertySubType && (
                    <tr>
                      <td>Sub Type:</td>
                      <td>{property.propertySubType}</td>
                    </tr>
                  )}
                  <tr>
                    <td>MLS #:</td>
                    <td>{property.mlsNumber}</td>
                  </tr>
                  {property.status && (
                    <tr>
                      <td>Status:</td>
                      <td>
                        <Badge bg={property.status === 'active' ? 'success' : 'secondary'}>
                          {property.status}
                        </Badge>
                      </td>
                    </tr>
                  )}
                  {property.daysOnMarket !== undefined && (
                    <tr>
                      <td>Days on Market:</td>
                      <td>{property.daysOnMarket}</td>
                    </tr>
                  )}
                  {property.lotSize && (
                    <tr>
                      <td>Lot Size:</td>
                      <td>{property.lotSize.toLocaleString()} acres</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Card.Body>
          </Card>

          {/* Virtual Tour */}
          {property.virtualTour && (
            <Card className="mb-4">
              <Card.Body>
                <h5>Virtual Tour</h5>
                <div className="d-grid">
                  <Button variant="info" href={property.virtualTour} target="_blank">
                    View Virtual Tour
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      {/* Similar Properties */}
      {similarProperties.length > 0 && (
        <Row className="mt-5">
          <Col>
            <h4 className="mb-4">Similar Properties</h4>
            <Row>
              {similarProperties.map((similar) => (
                <Col md={4} key={similar.mlsNumber} className="mb-4">
                  <Card 
                    className="h-100 cursor-pointer"
                    onClick={() => navigate(`/mls-portal/property/${similar.mlsNumber}`)}
                  >
                    <Card.Img 
                      variant="top" 
                      src={similar.images?.[0] || 'https://via.placeholder.com/300x200'}
                      style={{ height: '200px', objectFit: 'cover' }}
                    />
                    <Card.Body>
                      <h5 className="text-primary">{formatPrice(similar.price)}</h5>
                      <p className="mb-2">{similar.address.street}</p>
                      <p className="text-muted small">
                        {similar.bedrooms} bed • {similar.bathrooms} bath • {similar.sqft?.toLocaleString()} sqft
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
      )}

      {/* ARMLS IDX compliance — 23.3.3 source attribution + 23.3.4 VERBATIM
          disclaimer, required on every listing detail page (July 2026
          ActiveAgent audit cure). Do not reword the second sentence. */}
      <Row className="mt-4 mb-3">
        <Col>
          <div className="border-top pt-3 text-muted" style={{ fontSize: '11px', lineHeight: 1.5 }}>
            <p className="mb-1">
              Listing data provided by Arizona Regional Multiple Listing Service (ARMLS).
              Each listing is courtesy of its respective listing brokerage.
            </p>
            <p className="mb-1">
              All information should be verified by the recipient and none is guaranteed as accurate by ARMLS.
            </p>
            <p className="mb-0">
              Equal Housing Opportunity. &copy; {new Date().getFullYear()} Arizona Regional Multiple Listing Service, Inc.
            </p>
          </div>
        </Col>
      </Row>

      {/* Share Modal - using existing PropertyDetailsModal for consistency */}
      {showShareModal && property && (
        <PropertyDetailsModal
          property={{
            ...property,
            id: property.mlsNumber,
            city: property.address.city,
            state: property.address.state,
            zipCode: property.address.zip,
            zip: property.address.zip,
            squareFeet: property.sqft,
            propertyType: property.propertyType,
            mlsNumber: property.mlsNumber,
            price: property.price,
            bedrooms: property.bedrooms,
            bathrooms: property.bathrooms,
            address: property.address
          } as any}
          show={showShareModal}
          onHide={() => setShowShareModal(false)}
        />
      )}
    </Container>
  );
};

export default PropertyDetail;